import { useMemo, type ReactElement } from 'react';
import { Box, Flex, Group, Stack, Text } from '@mantine/core';
import { useBreakpointUp } from '@/shared/responsive/useBreakpoint';
import { ChevronRight } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import type { TopicDefinition } from './TopicCatalogTypes';
import type { AdHocAggregateResult } from '@/shared/analytics/AnalyticsTypes';
import { sliceToPath } from './TopicSliceKey';
import { formatCount, formatPercent, humanUser } from '@/shared/analytics/MetricFormatting';

interface DonutVizProps {
    definition: TopicDefinition;
    result: AdHocAggregateResult;
    basePath: string;
}

interface DonutSlice {
    raw: string;
    label: string;
    value: number;
    share: number;
    /** People behind the slice — omitted for the collapsed "Other" remainder (users overlap across segments). */
    users?: number;
}

const MAX_SLICES = 8;

/** Brand-family ramp for donut slices — the theme's mode-aware categorical chart ramp. */
const BRAND_RAMP = Array.from({ length: 8 }, (_, index) => `var(--bm-chart-${index + 1})`);

function displayLabel(definition: TopicDefinition, value: string): string {
    return definition.primaryField === 'user_id' ? humanUser(value) : value;
}

/**
 * Composition donut where each slice — and each legend row — drills into the L2
 * evidence for that segment. Slices beyond the top {@link MAX_SLICES} collapse
 * into a non-interactive "Other" remainder.
 */
export default function DonutViz({ definition, result, basePath }: DonutVizProps): ReactElement {
    const navigate = useNavigate();
    const metric = definition.headlineMetric;
    const total = result.totals[metric];

    const slices = useMemo<DonutSlice[]>(() => {
        const all: DonutSlice[] = result.rows.map((row) => {
            const raw = row.keys[definition.primaryField] ?? '—';
            const value = row.metrics[metric];
            return { raw, label: displayLabel(definition, raw), value, share: total === 0 ? 0 : value / total, users: row.metrics.users };
        });
        if (all.length <= MAX_SLICES) return all;
        const head = all.slice(0, MAX_SLICES - 1);
        const tail = all.slice(MAX_SLICES - 1);
        const tailValue = tail.reduce((sum, slice) => sum + slice.value, 0);
        head.push({ raw: '__other__', label: `Other (${tail.length})`, value: tailValue, share: total === 0 ? 0 : tailValue / total });
        return head;
    }, [result.rows, definition, metric, total]);

    const colorFor = (index: number): string => BRAND_RAMP[index % BRAND_RAMP.length] ?? 'var(--bm-color-brand-accent)';
    const isDrillable = (slice: DonutSlice): boolean => slice.raw !== '__other__' && slice.raw !== '—';
    const isWide = useBreakpointUp('sm');

    return (
        <Flex
            direction={isWide ? 'row' : 'column'}
            align={isWide ? 'center' : 'stretch'}
            gap={isWide ? 'xl' : 'md'}
            wrap="nowrap"
            style={{ border: '1px solid var(--bm-color-border)', borderRadius: 'var(--bm-radius)', background: 'var(--bm-color-card)', boxShadow: 'var(--bm-card-shadow)', padding: 'var(--mantine-spacing-lg)', minWidth: 0 }}
        >
            <Box style={{ width: isWide ? 220 : '100%', maxWidth: 240, height: 220, flexShrink: 0, alignSelf: 'center' }}>
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={slices}
                            dataKey="value"
                            nameKey="label"
                            innerRadius={62}
                            outerRadius={100}
                            paddingAngle={1}
                            stroke="var(--bm-color-body)"
                            strokeWidth={2}
                            onClick={(_, index) => {
                                const slice = slices[index];
                                if (slice !== undefined && isDrillable(slice)) navigate(sliceToPath(basePath, definition.id, { primary: slice.raw }));
                            }}
                        >
                            {slices.map((slice, index) => (
                                <Cell key={slice.raw} fill={colorFor(index)} style={{ cursor: isDrillable(slice) ? 'pointer' : 'default', outline: 'none' }} />
                            ))}
                        </Pie>
                        <Tooltip
                            formatter={(value: number) => [`${formatCount(value)} ${definition.unitLabel}`, '']}
                            contentStyle={{ background: 'var(--bm-color-body)', border: '1px solid var(--bm-color-border)', borderRadius: 'var(--bm-radius)', fontSize: 12 }}
                        />
                    </PieChart>
                </ResponsiveContainer>
            </Box>

            <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
                {slices.map((slice, index) => {
                    const swatch = <Box style={{ width: 10, height: 10, borderRadius: 2, background: colorFor(index), flexShrink: 0 }} />;
                    const body = (
                        <Group justify="space-between" gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                            <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                                {swatch}
                                <Text size="sm" fw={600} truncate style={{ minWidth: 0 }}>
                                    {slice.label}
                                </Text>
                                {slice.users !== undefined && slice.users > 0 && (
                                    <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                                        {formatCount(slice.users)} {slice.users === 1 ? 'user' : 'users'}
                                    </Text>
                                )}
                            </Group>
                            <Group gap={10} wrap="nowrap" style={{ flexShrink: 0 }}>
                                <Text size="sm" ff="monospace" fw={700}>
                                    {formatCount(slice.value)}
                                </Text>
                                <Text size="xs" ff="monospace" c="dimmed" style={{ width: 38, textAlign: 'right' }}>
                                    {formatPercent(slice.share)}
                                </Text>
                            </Group>
                        </Group>
                    );
                    if (!isDrillable(slice)) {
                        return (
                            <Box key={slice.raw} style={{ padding: '6px 8px' }}>
                                {body}
                            </Box>
                        );
                    }
                    return (
                        <Box
                            key={slice.raw}
                            component={Link}
                            to={sliceToPath(basePath, definition.id, { primary: slice.raw })}
                            className="topic-drill-row"
                            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', textDecoration: 'none', color: 'inherit' }}
                        >
                            {body}
                            <ChevronRight size={14} color="var(--bm-color-text-dimmed)" style={{ flexShrink: 0 }} />
                        </Box>
                    );
                })}
            </Stack>
        </Flex>
    );
}
